import { catchError, mergeMap } from 'rxjs/operators';
import { of, EMPTY, concat } from 'rxjs';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { ErrorModal } from '../components/modals/error-modal';

import Global from '../store/global-selectors';
import * as Actions from '../store/products/products.actions';
import * as ProductsApi from '../store/products/products.api';

import { entitiesLoad } from '../store/entities/entities.actions';
import { createLookupHash } from '../services/hash-service';
import { messages as productUpdateMessages } from '../products/product-details/messages';
import { processBulkLoadResponse } from '../store/products/product-bulk-load-processor';

// TODO add logic for all of the following error types
// ProductCannotBeDeactivated,
// ProductDoesNotExist,
// ProductDuplicateAliasSku,
const INVENTORY_ERROR_MESSAGE = 'This product can not be deactivated, because it still has remaining inventory stock.';
const DUPLICATE_ALIAS_SKU_ERROR_TYPE = 'ProductDuplicateSku';
const PRODUCT_ALIAS_HAS_MULTIPLE_MAPPINGS = 'ProductAliasSkuHasMultipleMappings';
const PRODUCT_SKU_CANNOT_MATCH_ALIAS_SKU = 'ProductSkuCannotEqualAliasSku';
const PRODUCT_DUPLICATE_ALIAS_SKU_WITH_STORE = 'ProductDuplicateAliasSkuWithStore';
const PRODUCT_BUNDLE_COMPONENT_IS_BUNDLE = 'ProductBundleComponentIsBundle';
const PRODUCT_SKU_DUPLICATES_UOM_SKU = 'ProductSkuDuplicatesUomSku';
const PRODUCT_ALIAS_SKU_DUPLICATES_UOM_SKU = 'ProductAliasSkuDuplicatesUomSku';
const notificationDuration = 4000;

const messages = defineMessages({
  'apply-changes-success-title': {
    description: 'notification.product-details.apply-changes.success.title',
    defaultMessage: 'Product Changes Successfully Applied',
    id: 'I7EuEf',
  },
  'apply-changes-success-content': {
    description: 'notification.product-details.apply-changes.success.content',
    defaultMessage: 'The changes you made to your product have successfully been applied to open orders.',
    id: 'O+g0h5',
  },
  'apply-changes-error-title': {
    description: 'notification.product-details.apply-changes.error.title',
    defaultMessage: 'Unable to apply saved product changes',
    id: 'lloNUc',
  },
  'apply-changes-error-content': {
    description: 'notification.product-details.apply-changes.error.content',
    defaultMessage: 'Refresh the page and try again.',
    id: 'IfWwuj',
  },
  'apply-changes-reload-error-title': {
    description: 'notification.product-details.apply-changes.reload-error.title',
    defaultMessage: 'Product Changes Applied',
    id: 'fktCa5',
  },
  'apply-changes-reload-error-content': {
    description: 'notification.product-details.apply-changes.reload-error.content',
    defaultMessage: 'Product changes were applied, but we could not load the changes. <refresh>Refresh</refresh> to load changes.',
    id: 'CSOCvS',
  },
});

class ProductDetailsServiceClass {
  returnProductEntityFromMemory(state, productId) {
    return Global.selectEntitiesProductsById(state, { productId });
  }

  fetchProductDetails(state, productId, modalId) {
    if (!productId && productId !== 0) {
      return of(Actions.getProductDetailsSuccess({}));
    }

    return ProductsApi.bulkLoadProductsAsObservable([productId]).pipe(
      mergeMap((response) => {
        const products = processBulkLoadResponse(response);
        const entitiesToLoad = {
          entities: {
            products: createLookupHash(products, 'productId'),
          },
        };

        const productObj = products.find((p) => p.productId === `${productId}`);
        return concat(of(entitiesLoad(entitiesToLoad)), of(Actions.getProductDetailsSuccess(productObj)));
      }),
      catchError(() => {
        if (modalId) {
          ModalService.closeModalById(modalId);
        }
        ModalService.launchModal({
          component: ErrorModal,
          props: {
            title: <FormattedMessage defaultMessage='Error' description='productDetailsError.title' id='Tqi2oP' />,
            message: (
              <FormattedMessage
                defaultMessage='There was an error opening product details.'
                description='productDetailsError.message'
                id='81OOeT'
              />
            ),
          },
        });

        return EMPTY;
      }),
    );
  }

  fetchProductCategories(modalId) {
    return ProductsApi.getProductCategoriesAsObservable().pipe(
      mergeMap((response) => {
        const mappedCategories = response.map((category) => ({
          ...category,
          categoryId: `${category.categoryId}`,
        }));
        const entitiesToLoad = {
          entities: {
            productCategories: createLookupHash(mappedCategories, 'categoryId'),
          },
        };

        return of(entitiesLoad(entitiesToLoad));
      }),
      catchError(() => {
        if (modalId) {
          ModalService.closeModalById(modalId);
        }
        ModalService.launchModal({
          component: ErrorModal,
          props: {
            title: <FormattedMessage defaultMessage='Error' description='productDetailsError.title' id='Tqi2oP' />,
            message: (
              <FormattedMessage
                defaultMessage='There was an error opening product details.'
                description='productDetailsError.message'
                id='81OOeT'
              />
            ),
          },
        });

        return EMPTY;
      }),
    );
  }

  getProductDetailsObservable(state, productId, modalId) {
    if (!productId && productId !== 0) {
      return of(Actions.getProductDetailsSuccess({}));
    }
    const product = this.returnProductEntityFromMemory(state, productId);
    if (product) {
      if (product.parentProductId && !product.parentProduct) {
        return this.fetchProductDetails(state, productId, modalId);
      }
      return of(Actions.getProductDetailsSuccess(product));
    }

    return this.fetchProductDetails(state, productId, modalId);
  }

  didProductUpdateFailDueToInventory(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors
      .filter((err) => err && err.Message)
      .map((err) => err.Message)
      .includes(INVENTORY_ERROR_MESSAGE);
  }

  didProductUpdateFailDueToSkuAlias(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors.some((err) => err.Code === DUPLICATE_ALIAS_SKU_ERROR_TYPE || err.Code === PRODUCT_ALIAS_HAS_MULTIPLE_MAPPINGS);
  }

  didProductUpdateFailDueToProductAndAliasSku(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors.some((err) => err.Code === PRODUCT_SKU_CANNOT_MATCH_ALIAS_SKU);
  }

  didProductUpdateFailDueToDuplicateAliasWithStore(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors.some((err) => err.Code === PRODUCT_DUPLICATE_ALIAS_SKU_WITH_STORE);
  }

  didProductUpdateFailDueToBundleComponent(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors.some((err) => err.Code === PRODUCT_BUNDLE_COMPONENT_IS_BUNDLE);
  }

  didProductUpdateFailDueToUomSkuConflict(errors) {
    if (!errors || !errors.length) {
      return false;
    }
    return errors.some((err) => err.Code === PRODUCT_SKU_DUPLICATES_UOM_SKU || err.Code === PRODUCT_ALIAS_SKU_DUPLICATES_UOM_SKU);
  }

  skuList(errors) {
    if (!errors || !errors.length) {
      return [];
    }
    return [...new Set(errors.filter((err) => err && err.ErrorContext).map((err) => err.ErrorContext))];
  }

  productDetailsErrorMeta(errors) {
    return {
      didProductUpdateFailDueToInventory: this.didProductUpdateFailDueToInventory(errors),
      didProductUpdateFailDueToSkuAlias: this.didProductUpdateFailDueToSkuAlias(errors),
      didProductUpdateFailDueToProductAndAliasSku: this.didProductUpdateFailDueToProductAndAliasSku(errors),
      didProductUpdateFailDueToDuplicateAliasWithStore: this.didProductUpdateFailDueToDuplicateAliasWithStore(errors),
      didProductUpdateFailDueToBundleComponent: this.didProductUpdateFailDueToBundleComponent(errors),
      didProductUpdateFailDueToUomSkuConflict: this.didProductUpdateFailDueToUomSkuConflict(errors),
      errorSkuList: this.skuList(errors),
    };
  }

  handleProductUpdateErrors(errorsList) {
    const errorMeta = this.productDetailsErrorMeta(errorsList);
    if (errorMeta.didProductUpdateFailDueToInventory) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: <FormattedMessage {...productUpdateMessages.errorNotificationMessageInventory} />,
        duration: notificationDuration,
      });
      return;
    }
    if (errorMeta.didProductUpdateFailDueToSkuAlias) {
      const { errorSkuList } = errorMeta;
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: (
          <FormattedMessage
            {...productUpdateMessages.errorNotificationMessageDuplicateSku}
            values={{ duplicateSkuList: errorSkuList.join(', ') }}
          />
        ),
        duration: notificationDuration,
      });
      return;
    }
    if (errorMeta.didProductUpdateFailDueToProductAndAliasSku) {
      const { errorSkuList } = errorMeta;
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: (
          <FormattedMessage
            {...productUpdateMessages.errorNotificationProduSkuCannotMatchAlias}
            values={{ skuList: errorSkuList.join(', ') }}
          />
        ),
        duration: notificationDuration,
      });
      return;
    }
    if (errorMeta.didProductUpdateFailDueToDuplicateAliasWithStore) {
      const { errorSkuList } = errorMeta;
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: (
          <FormattedMessage
            {...productUpdateMessages.errorNotificationProduDuplicateAliasWithStore}
            values={{ skuList: errorSkuList.join(', ') }}
          />
        ),
        duration: notificationDuration,
      });
      return;
    }
    if (errorMeta.didProductUpdateFailDueToBundleComponent) {
      const bundleError = errorsList.find((err) => err.Code === PRODUCT_BUNDLE_COMPONENT_IS_BUNDLE);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: bundleError?.Message || 'Bundle component cannot be a bundle itself',
        duration: notificationDuration,
      });
      return;
    }
    if (errorMeta.didProductUpdateFailDueToUomSkuConflict) {
      const { errorSkuList } = errorMeta;
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
        content: (
          <FormattedMessage
            {...productUpdateMessages.errorNotificationMessageUomSkuConflict}
            values={{ duplicateSkuList: errorSkuList.join(', ') }}
          />
        ),
        duration: notificationDuration,
      });
      return;
    }

    this.showGeneralErrorNotification();
  }

  showGeneralErrorNotification() {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...productUpdateMessages.errorNotificationTitle} />,
      content: <FormattedMessage {...productUpdateMessages.errorNotificationMessage} />,
      duration: notificationDuration,
    });
  }

  handleCompletedApplyToOpenFulfillmentPlansMessage(message) {
    const errorNotification = {
      title: <FormattedMessage {...messages['apply-changes-error-title']} />,
      content: <FormattedMessage {...messages['apply-changes-error-content']} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      dontHide: true,
    };

    const successNotification = {
      title: <FormattedMessage {...messages['apply-changes-success-title']} />,
      content: <FormattedMessage {...messages['apply-changes-success-content']} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      duration: 3000,
    };

    const refresh = (text) => (
      // oxlint-disable-next-line jsx-a11y/anchor-is-valid
      <a href='' onClick={() => window.location.reload()}>
        {text}
      </a>
    );

    const reloadDataErrorNotification = {
      title: <FormattedMessage {...messages['apply-changes-reload-error-title']} />,
      content: (
        <FormattedMessage
          {...messages['apply-changes-reload-error-content']}
          values={{
            refresh,
          }}
        />
      ),
      notificationType: NOTIFICATION_TYPE_ERROR,
      dontHide: true,
    };

    if (message?.data?.success) {
      return Actions.applyProductUpdateToOpenFulfillmentPlanSuccess(successNotification, reloadDataErrorNotification);
    }

    NotificationService.showNotification(errorNotification);
  }
}

export const ProductDetailsService = new ProductDetailsServiceClass();
